// 12's authorization reads, built on 07's `useRbac()` (contract 12 §12). Two gates: whether the
// Connections settings entry is visible, and whether a given provider can be connected at all.
//
// The predicate is 12's OWN, deliberately. Module 22 stores, sorts and renders a registrant's nav
// entry and never inspects its permission keys (contract 22 §5); a registrant reaching back for 22's
// `useSettingsRbac` would invert the very dependency the registry exists to avoid.
//
// v3's rule: `useWorkspaceSettings.js:218` —
//   `accountStore.selectedAccountLevel !== 'viewer' || accountStore.accountUserId === accountStore.userId`
// 07 publishes neither `accountLevel` nor a user-id comparison (CM-22-06 is unlanded), so both halves
// read off the surface it DOES publish: `rbac.roles` (the role labels `accountLevel` is computed
// from) and `rbac.is_account_holder` (the server-computed form of the id comparison — audit
// ORP-18613 forbids the client re-deriving authority from raw ids).
import { computed, type ComputedRef } from 'vue'

import type { IntegrationProvider } from '../models/providers.types'
import { useIntegrationStore } from '../stores/integrationStore'

import { useRbac } from '@/features/account'

/** Role labels above `viewer` — v3's `selectedAccountLevel !== 'viewer'` on 07's current surface. */
const ABOVE_VIEWER_ROLES = new Set(['editor', 'admin', 'owner'])
const ADMIN_ROLES = new Set(['admin', 'owner'])

export interface UseIntegrationsRbac {
  /** Gates the Connections settings entry. Cold-boot safe: pre-`ready` there are no roles and
   *  `isAccountHolder` is false, so the entry stays hidden rather than flashing in. */
  canViewConnections: ComputedRef<boolean>
  /**
   * Whether the caller may install Slack and choose its notification channel — account admin or
   * owner, v3's `isAdminOrOwner`. Cosmetic: the gateway enforces workspace admin on every call and
   * the page shows its refusal inline, so a workspace-level admin who is not an account admin sees
   * the controls only as far as this mirror allows.
   */
  canManageSlack: ComputedRef<boolean>
  /** The package flag behind Slack notifications — the same `notification_slack` gate the
   *  Notifications pane puts on its Slack row. */
  canUseSlackNotifications: ComputedRef<boolean>
  /**
   * Whether this account may connect a provider at all — the ONE implementation (AC 6).
   *
   * v3 had three copies of this predicate, on three components, all reading two stores inline:
   * `Connections.vue:175`, `IntegrationsLoop.vue:73` and `IntegrationsCard.vue:162` (defect D12).
   * Each decided something different with it — whether the "Upgrade to get these destinations"
   * section appears, which loop a tile belongs to, and whether the tile's button says Connect or
   * Upgrade — so a drift between them silently produced a tile in the wrong section.
   *
   * FALSE IS NOT "HIDE" (AC 3): the caller renders the provider in the upgrade section with a
   * pricing CTA. v3 hid nothing either, but only because a second `IntegrationsLoop` with
   * `checkType: 'permission_denied'` re-ran the whole registry with the predicate negated.
   */
  canConnect: (provider: IntegrationProvider) => boolean
}

export function useIntegrationsRbac(): UseIntegrationsRbac {
  const rbac = useRbac()

  return {
    canViewConnections: computed(
      () =>
        rbac.isAccountHolder.value ||
        rbac.roles.value.some((role) => ABOVE_VIEWER_ROLES.has(role.toLowerCase())),
    ),

    canManageSlack: computed(
      () =>
        rbac.isAccountHolder.value ||
        rbac.roles.value.some((role) => ADMIN_ROLES.has(role.toLowerCase())),
    ),

    canUseSlackNotifications: computed(() => rbac.hasPermission('notification_slack')),

    // The store is resolved lazily, per call, for the same reason `useIntegrations` defers 07: the
    // settings nav evaluates `canViewConnections` on every render and must not need a Pinia
    // instance carrying integration state to do it.
    canConnect: (provider) => {
      // v3 wrote `getPermission(`${category}_`) > 0` — a relational compare on a value typed
      // `boolean | number | string | string[] | null`. `Number(x) > 0` is that expression's exact
      // JS semantics (`true`→1, `'3'`→3, `'x'`→NaN→false, `null`→0, `undefined`→NaN) made explicit,
      // rather than the looser `Boolean(x)` that 07's `hasPermission` applies.
      const limit = Number(rbac.permissions.value[`${provider.category}_`])
      if (!(limit > 0)) return false
      return useIntegrationStore().allowedIntegrations.includes(provider.id)
    },
  }
}
